import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useUpdateNsuStatusMutation } from "@/services/conciliation/nsu";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { NsuStatusEnum } from "@/enum/nsu-status";
import { CheckCircle, XCircle } from "lucide-react";
import { useState } from "react";

interface NsuStatusModalProps {
  nsuId: number;
}

export function NsuStatusModal({
  nsuId
}: NsuStatusModalProps) {
  const { handleModal } = useDialogModal();
  const [updateStatus, { isLoading }] = useUpdateNsuStatusMutation();
  const [selectedAction, setSelectedAction] = useState<NsuStatusEnum.APPROVED | NsuStatusEnum.REJECTED | null>(null);

  const handleStatusUpdate = async () => {
    if (!selectedAction) {
      toast({
        variant: "destructive",
        description: "Selecione uma ação (Aprovar ou Rejeitar)",
        duration: 3000,
      });
      return;
    }

    try {
      await updateStatus({
        id: nsuId,
        status: selectedAction,
      }).unwrap();

      toast({
        variant: "default",
        description: `NSU ${selectedAction === NsuStatusEnum.APPROVED ? "aprovado" : "rejeitado"} com sucesso`,
        duration: 3000,
      });

      closeModal();
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Erro ao atualizar status do NSU",
        duration: 3000,
      });
    }
  };

  const closeModal = () => {
    handleModal({ isOpen: false });
    setSelectedAction(null);
  };

  return (
    <div className="w-full max-w-sm mx-auto bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setSelectedAction(NsuStatusEnum.APPROVED)}
            className={`w-full p-4 border-2 rounded-lg text-left transition-all duration-200 ${
              selectedAction === NsuStatusEnum.APPROVED
                ? "border-green-500 bg-green-50"
                : "border-gray-200 hover:border-green-300 hover:bg-green-25"
            }`}
          >
            <div className="flex items-center gap-3">
              <CheckCircle className={`h-5 w-5 ${
                selectedAction === NsuStatusEnum.APPROVED ? "text-green-600" : "text-gray-400"
              }`} />
              <div>
                <div className="font-medium text-gray-900">Aprovar</div>
                <div className="text-sm text-gray-600">
                  Aprovar este NSU
                </div>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setSelectedAction(NsuStatusEnum.REJECTED)}
            className={`w-full p-4 border-2 rounded-lg text-left transition-all duration-200 ${
              selectedAction === NsuStatusEnum.REJECTED
                ? "border-red-500 bg-red-50"
                : "border-gray-200 hover:border-red-300 hover:bg-red-25"
            }`}
          >
            <div className="flex items-center gap-3">
              <XCircle className={`h-5 w-5 ${
                selectedAction === NsuStatusEnum.REJECTED ? "text-red-600" : "text-gray-400"
              }`} />
              <div>
                <div className="font-medium text-gray-900">Rejeitar</div>
                <div className="text-sm text-gray-600">
                  Rejeitar este NSU
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={closeModal}
          disabled={isLoading}
          className="flex-1"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleStatusUpdate}
          disabled={isLoading || !selectedAction}
          className={`flex-1 text-white ${
            selectedAction === NsuStatusEnum.APPROVED
              ? "bg-green-600 hover:bg-green-700"
              : selectedAction === NsuStatusEnum.REJECTED
              ? "bg-red-600 hover:bg-red-700"
              : ""
          }`}
        >
          {isLoading ? "Processando..." :
           selectedAction === NsuStatusEnum.APPROVED ? "Aprovar" :
           selectedAction === NsuStatusEnum.REJECTED ? "Rejeitar" :
           "Confirmar"}
        </Button>
      </div>
    </div>
  );
}
